<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import ButtonV4 from 'next/button/Button.vue';
import BillingAPI from 'dashboard/api/billing';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();

const supportInfo = ref({
  email: '',
  whatsapp: '',
});
const isLoadingSupport = ref(false);

const accountId = computed(() => route.params.accountId);

const currentAccount = computed(() =>
  store.getters['accounts/getAccount'](accountId.value)
);

const accountName = computed(() => currentAccount.value?.name || '');

const toggleSupportWidgetVisibility = () => {
  if (window.$chatwoot) {
    window.$chatwoot.toggleBubbleVisibility('show');
  }
};

const setupListenerForWidgetEvent = () => {
  window.addEventListener('chatwoot:on-message', () => {
    toggleSupportWidgetVisibility();
  });
};

const fetchSupportInfo = async () => {
  isLoadingSupport.value = true;
  try {
    const { data } = await BillingAPI.getSupportInfo();
    supportInfo.value = {
      email: data.email || '',
      whatsapp: data.whatsapp || '',
    };
  } catch (error) {
    // Silently fail - support info is optional
    // eslint-disable-next-line no-console
    console.warn('Failed to fetch support info:', error);
  } finally {
    isLoadingSupport.value = false;
  }
};

const goToBilling = () => {
  router.push({
    name: 'billing_settings_index',
    params: { accountId: accountId.value },
  });
};

const openWhatsApp = () => {
  if (supportInfo.value.whatsapp) {
    const phone = supportInfo.value.whatsapp.replace(/\D/g, '');
    const message = encodeURIComponent(
      t('APP_GLOBAL.ACCOUNT_SUSPENDED.WHATSAPP_MESSAGE', {
        account: accountName.value,
      })
    );
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
  }
};

const sendEmail = () => {
  if (supportInfo.value.email) {
    const subject = encodeURIComponent(
      t('APP_GLOBAL.ACCOUNT_SUSPENDED.EMAIL_SUBJECT', {
        account: accountName.value,
      })
    );
    const body = encodeURIComponent(
      t('APP_GLOBAL.ACCOUNT_SUSPENDED.EMAIL_BODY', {
        account: accountName.value,
      })
    );
    window.location.href = `mailto:${supportInfo.value.email}?subject=${subject}&body=${body}`;
  }
};

onMounted(() => {
  toggleSupportWidgetVisibility();
  setupListenerForWidgetEvent();
  fetchSupportInfo();
});
</script>

<template>
  <div
    class="flex items-center justify-center h-full w-full bg-n-slate-1 dark:bg-n-slate-1"
  >
    <div class="max-w-lg w-full mx-4">
      <!-- Card Container -->
      <div
        class="bg-white dark:bg-n-slate-2 rounded-2xl shadow-lg p-8 text-center"
      >
        <!-- Icon -->
        <div class="flex justify-center mb-6">
          <div
            class="w-20 h-20 rounded-full bg-n-red-2 dark:bg-n-red-9/20 flex items-center justify-center"
          >
            <span class="i-lucide-ban text-4xl text-n-red-9" />
          </div>
        </div>

        <!-- Title -->
        <h1 class="text-2xl font-bold text-n-slate-12 mb-3">
          {{ t('APP_GLOBAL.ACCOUNT_SUSPENDED.TITLE') }}
        </h1>

        <!-- Message -->
        <p class="text-n-slate-11 mb-6 leading-relaxed">
          {{ t('APP_GLOBAL.ACCOUNT_SUSPENDED.MESSAGE') }}
        </p>

        <!-- Reason Card -->
        <div class="p-4 rounded-xl bg-n-amber-2 border border-n-amber-6 mb-6">
          <div class="flex items-start gap-3">
            <span
              class="i-lucide-alert-triangle text-n-amber-11 text-xl flex-shrink-0 mt-0.5"
            />
            <div class="text-left">
              <p class="text-sm font-medium text-n-amber-12">
                {{ t('APP_GLOBAL.ACCOUNT_SUSPENDED.REASON_TITLE') }}
              </p>
              <p class="text-sm text-n-amber-11 mt-1">
                {{ t('APP_GLOBAL.ACCOUNT_SUSPENDED.REASON_MESSAGE') }}
              </p>
            </div>
          </div>
        </div>

        <!-- Primary Action: Go to Billing -->
        <ButtonV4 class="w-full mb-4" solid blue lg @click="goToBilling">
          <span class="i-lucide-credit-card mr-2" />
          {{ t('APP_GLOBAL.ACCOUNT_SUSPENDED.GO_TO_BILLING') }}
        </ButtonV4>

        <!-- Divider -->
        <div class="flex items-center gap-4 my-6">
          <div class="flex-1 h-px bg-n-slate-4" />
          <span class="text-sm text-n-slate-10">
            {{ t('APP_GLOBAL.ACCOUNT_SUSPENDED.OR_CONTACT_SUPPORT') }}
          </span>
          <div class="flex-1 h-px bg-n-slate-4" />
        </div>

        <!-- Support Contact Options -->
        <div class="flex gap-3">
          <!-- WhatsApp Button -->
          <ButtonV4
            v-if="supportInfo.whatsapp"
            class="flex-1"
            faded
            teal
            @click="openWhatsApp"
          >
            <span class="i-lucide-message-circle mr-2" />
            {{ t('APP_GLOBAL.ACCOUNT_SUSPENDED.WHATSAPP_BUTTON') }}
          </ButtonV4>

          <!-- Email Button -->
          <ButtonV4
            v-if="supportInfo.email"
            class="flex-1"
            faded
            slate
            @click="sendEmail"
          >
            <span class="i-lucide-mail mr-2" />
            {{ t('APP_GLOBAL.ACCOUNT_SUSPENDED.EMAIL_BUTTON') }}
          </ButtonV4>
        </div>

        <!-- No support info message -->
        <p
          v-if="
            !supportInfo.whatsapp && !supportInfo.email && !isLoadingSupport
          "
          class="text-sm text-n-slate-10 mt-4"
        >
          {{ t('APP_GLOBAL.ACCOUNT_SUSPENDED.CONTACT_ADMIN') }}
        </p>
      </div>

      <!-- Footer Note -->
      <p class="text-center text-sm text-n-slate-10 mt-6">
        {{ t('APP_GLOBAL.ACCOUNT_SUSPENDED.FOOTER_NOTE') }}
      </p>
    </div>
  </div>
</template>
